import { aPostDirectShare } from '../../graphql/generated/fixtures';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { ProvisionType, UserType } from '../../graphql/generated/graphql';
import postPolicyCheck from '../../post/postPolicyCheck';
import topicPolicyCheck from '../../topic/topicPolicyCheck';

const PROVISION_FRAGMENT = gql(`
  # eslint-disable-next-line @graphql-eslint/require-selections
  fragment provisionTopicPolicyCheck_provision on Provision {
    type
    members {
      ... on Topic {
        id
      }
      ... on Post {
        id
      }
      ... on Group {
        id
      }
    }
  }
`);

type ProvisionFragment = DocumentType<typeof PROVISION_FRAGMENT>;

// eslint-disable-next-line slab-graphql/no-unused-fields
const PROVISION_POST_POLICY_CHECK_TOPIC_FRAGMENT = gql(`
  fragment provisionTopicPolicyCheck_provision_topic on Topic {
    # We do not need any actual membership arrays here
    # since we only care about our fake user's access
    id
    memberEditable
    privacy
    memberGroups {
      id
    }
    ownerGroups {
      id
    }
  }
`);

type TopicFragment = DocumentType<
  typeof PROVISION_POST_POLICY_CHECK_TOPIC_FRAGMENT
>;

// eslint-disable-next-line slab-graphql/no-unused-fields
const PROVISION_POST_POLICY_CHECK_POST_FRAGMENT = gql(`
  fragment provisionPostPolicyCheck_post on Post {
    id
    privacy
    topics {
      id
      ...provisionTopicPolicyCheck_provision_topic
      inheritedAncestors {
        ...provisionTopicPolicyCheck_provision_topic
      }
    }
    ...postPolicyCheck_post
  }
`);

type PostFragment = DocumentType<
  typeof PROVISION_POST_POLICY_CHECK_POST_FRAGMENT
>;

const addUserToGroups = <Target extends { id: string }, User>(
  targetGroups: Target[],
  membershipGroups: { id: string }[],
  user: User,
): (Target & { allUsers: User[] })[] => {
  const membershipGroupIdSet = new Set(
    membershipGroups.map((group) => group.id),
  );

  return targetGroups.map((group) => ({
    ...group,
    allUsers: membershipGroupIdSet.has(group.id) ? [user] : [],
  }));
};

type Topic = TopicFragment & {
  inheritedAncestors?: TopicFragment[];
};

const addUserToTopic = (
  user: { id: string },
  topic: Topic,
  provision: ProvisionFragment,
): Extract<TopicPolicyCheckParameters[1], { __typename?: 'Topic' }> => {
  const { members } = provision;
  const groups = members.filter((m) => m.__typename === 'Group');
  const topics = members.filter((m) => m.__typename === 'Topic');
  const memberTopicIdSet = new Set(topics.map((t) => t.id));

  return {
    ...topic,
    inheritedAncestors:
      topic.inheritedAncestors?.map((ancestor) =>
        addUserToTopic(user, ancestor, provision),
      ) || [],
    members: memberTopicIdSet.has(topic.id) ? [user] : [],
    owners: [],
    memberGroups: addUserToGroups(topic.memberGroups, groups, user),
    ownerGroups: addUserToGroups(topic.ownerGroups, groups, user),
  };
};

const UserTypeMap = {
  [ProvisionType.Admin]: UserType.Admin,
  [ProvisionType.Guest]: UserType.Guest,
  [ProvisionType.User]: UserType.User,
};

const getFakeUser = (provision: ProvisionFragment) => ({
  id: '_provision_access_check_fake_user',
  type: UserTypeMap[provision.type || ProvisionType.User],
  membershipGroups: [],
});

type PostPolicyCheckParameters = Parameters<typeof postPolicyCheck>;
type TopicPolicyCheckParameters = Parameters<typeof topicPolicyCheck>;

export const provisionPostPolicyCheck = (
  action: PostPolicyCheckParameters[0],
  post: PostFragment,
  provision: ProvisionFragment,
) => {
  const fakeUser = getFakeUser(provision);

  const posts = provision.members.filter((m) => m.__typename === 'Post');

  const fakePost = {
    ...post,
    sharing: aPostDirectShare({
      editorIds: posts.some((p) => p.id === post.id) ? [fakeUser.id] : [],
    }),
    topics: post.topics.map((topic) =>
      addUserToTopic(fakeUser, topic, provision),
    ),
  };

  return postPolicyCheck(action, fakePost, fakeUser);
};

export const provisionTopicPolicyCheck = (
  action: TopicPolicyCheckParameters[0],
  topic: Topic,
  provision: ProvisionFragment,
) => {
  const fakeUser = getFakeUser(provision);
  const fakeTopic = addUserToTopic(fakeUser, topic, provision);

  return topicPolicyCheck(action, fakeTopic, fakeUser);
};
